import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { HelperText } from '../../../form-kit';
import { LinkField, LinkRow } from '../../../onboarding/invite/invite-qr-modal';
import { Collapse, StatusLabel } from '../../../../motion';
import { sharePass, stopSharing } from '../../../../api/tenant-passes-client';
import { ResidentPass } from '../../../../interfaces/visitor-pass';
import { countOf } from '../../../../function-utils/plural';
import toast from '../../../../utils/toast';
import { readPassError } from '../../../../utils/pass-errors';
import { palette, spacing, typography } from '../../../../style-utils/theme';
import { whenAt } from '../portal-common';
import { Card, Section, SectionHead, SectionTitle } from '../portal-kit.styled';
import { FormAlert } from '../../../pass-editor/pass-editor.styled';

/**
 * SHARE A PASS WITH THE GUEST (resident-portal Plan 6, RP-6.2).
 *
 * "Share with Maria" makes a link to Maria's pass page — her first name, the
 * building, when she is expected, and a code for the front desk. The link is
 * shown ONCE, here, when it is made: the server keeps only a fingerprint of it,
 * so after a reload the section can say "shared since …" but never show the
 * link again. "New link" makes another (the old one stops working); "Stop
 * sharing" ends it at once.
 *
 * The phone's share sheet (navigator.share) is offered where the browser has
 * one; the link and its Copy button are always there too — they are the
 * fallback, and they are what a desktop browser (and the journey) reads.
 *
 * A pass waiting for approval, an ended pass, and a building that has switched
 * passes off offer nothing to share (the server refuses too); a pass that is
 * shared can always be stopped.
 *
 * EVERY REFUSAL IN ITS OWN WORDS (RP-9.6). Both actions read a failure through
 * utils/pass-errors: a rate limit says so, a pass ended on another phone says it
 * is not here any more AND the page re-reads, and the connection is blamed only
 * when no answer came back.
 *
 * "NOT HERE ANY MORE" IS A TOAST AS WELL. The re-read that follows it removes
 * this whole section (an ended pass has nothing to share), and a sentence
 * written only inside it was on screen for one frame — the page changed and
 * nothing said why (found by journey act J-9.3, gate g10061… ). The toast
 * outlives the section.
 */

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
  padding: ${spacing.md};
`;

const Lead = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

/* The link row sits flush under the lead: the invite row's own top margin is
   for a centred modal. */
const ShareLinkRow = styled(LinkRow)`
  margin: 0;
`;

export const SHARE_HELP = 'Anyone with this link can show it at the desk. The desk still checks who it is.';
export const SHARE_GONE = 'This pass is not here any more. It may have ended.';

const canNativeShare = (): boolean =>
  typeof navigator !== 'undefined' && typeof (navigator as Navigator).share === 'function';

interface Props {
  pass: ResidentPass;
  /** The first name the page uses for this visitor. */
  first: string;
  /** The pass as the server now has it, after a share or a stop. */
  onChanged: (pass: ResidentPass | null) => void;
}

export const PassShare: React.FC<Props> = ({ pass, first, onChanged }) => {
  // The link, only in this page's memory, only after THIS page made it.
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState<'share' | 'stop' | null>(null);
  const busyRef = useRef(false);
  const [problem, setProblem] = useState('');
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [confirmingStop, setConfirmingStop] = useState(false);

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    []
  );

  // A pass that stopped being shareable (ended, off, a stop from another
  // phone in the household) drops the link it was showing.
  const shared = !!pass.shared;
  useEffect(() => {
    if (!shared) setUrl('');
  }, [shared]);

  const canShare = pass.can_share === true;
  if (!canShare && !shared) return null;

  const share = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy('share');
    setProblem('');
    try {
      const made = await sharePass(pass.pass_id);
      setUrl(made.url);
      setCopied(false);
      onChanged(made.pass);
      toast.success(shared ? `Made a new link for ${first}. The old one no longer works.` : `Link ready for ${first}.`);
    } catch (error) {
      const reading = readPassError(error, {
        fallback: `Could not make a link for ${first}. Try again.`,
        offline: `Could not make a link for ${first}. Check your connection and try again.`,
        notFound: SHARE_GONE,
      });
      setProblem(
        // A 409 the server did not name: the pass cannot be shared as it stands.
        reading.status === 409 && !reading.code
          ? `${first}'s pass can't be shared right now.`
          : reading.message
      );
      // Ended or handed on from another phone: say so where it will still be
      // read once this section has gone, and show what is true now.
      if (reading.gone) {
        toast.error(reading.message);
        onChanged(null);
      }
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const stop = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy('stop');
    setProblem('');
    try {
      const next = await stopSharing(pass.pass_id);
      setUrl('');
      onChanged(next);
      toast.success(`${first}'s link no longer works.`);
    } catch (error) {
      const reading = readPassError(error, {
        fallback: `Could not stop sharing ${first}'s pass. Try again.`,
        offline: `Could not stop sharing ${first}'s pass. Check your connection and try again.`,
        notFound: SHARE_GONE,
      });
      setProblem(reading.message);
      if (reading.gone) {
        setUrl('');
        toast.error(reading.message);
        onChanged(null);
      }
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  // The share sheet must open inside the tap itself (Safari will not open it
  // after an await), so it is its own button, pressed once the link exists.
  const send = () => {
    if (!url || !canNativeShare()) return;
    navigator
      .share({ title: `${first}'s visitor pass`, text: `Your visitor pass for ${first}`, url })
      .catch(() => {
        // Closing the sheet is not an error; the link and Copy are still here.
      });
  };

  const since = pass.shared?.since ? whenAt(pass.shared.since, pass.building_timezone) : '';
  const opened = pass.shared?.opened ?? 0;

  return (
    <Section aria-labelledby="pass-share-title">
      <SectionHead>
        <SectionTitle id="pass-share-title">Share with the visitor</SectionTitle>
      </SectionHead>
      <Card>
        <Body>
          {url ? (
            <>
              <Lead>{`Send ${first} this link. It opens their pass and a code to show at the front desk.`}</Lead>
              <ShareLinkRow>
                <LinkField
                  readOnly
                  value={url}
                  aria-label={`Link for ${first}`}
                  onFocus={e => e.target.select()}
                />
                <AppButton variant="secondary" onClick={copy}>
                  <StatusLabel state={copied ? 'copied' : 'copy'} labels={{ copy: 'Copy link', copied: 'Copied' }} />
                </AppButton>
                {canNativeShare() ? (
                  <AppButton variant="secondary" onClick={send}>
                    Send link
                  </AppButton>
                ) : null}
              </ShareLinkRow>
              <HelperText>{SHARE_HELP}</HelperText>
            </>
          ) : shared ? (
            <Lead>
              {[
                since ? `Shared with ${first} since ${since}.` : `Shared with ${first}.`,
                `Opened ${countOf(opened, 'time')}.`,
                'The link is shown only once. To send it again, make a new link.',
              ].join(' ')}
            </Lead>
          ) : (
            <Lead>{`Send ${first} a link to this pass. They show it at the front desk, and the desk still checks who it is.`}</Lead>
          )}

          <Collapse open={!!problem}>
            <FormAlert role="alert" message={problem || undefined} />
          </Collapse>

          <Buttons>
            {canShare ? (
              <AppButton
                variant={shared ? 'ghost' : 'secondary'}
                disabled={!!busy}
                onClick={share}
                aria-label={shared ? `New link for ${first}` : `Share with ${first}`}
              >
                <StatusLabel
                  state={busy === 'share' ? 'busy' : 'idle'}
                  labels={{ idle: shared ? 'New link' : `Share with ${first}`, busy: 'Making a link…' }}
                />
              </AppButton>
            ) : null}
            {shared ? (
              <AppButton variant="ghost" disabled={!!busy} onClick={() => setConfirmingStop(true)}>
                Stop sharing
              </AppButton>
            ) : null}
          </Buttons>
        </Body>
      </Card>

      <ConfirmDialog
        open={confirmingStop}
        title={`Stop sharing ${first}'s pass?`}
        message={`The link stops working at once. ${first}'s pass itself stays as it is.`}
        primaryLabel="Stop sharing"
        primaryVariant="destructive"
        dismissLabel="Keep sharing"
        onPrimary={async () => {
          await stop();
          setConfirmingStop(false);
        }}
        onDismiss={() => setConfirmingStop(false)}
      />
    </Section>
  );
};
